import { supabase, isAdminConfigured, getAdminClient, handleAdminError } from '../lib/supabase';

class UserService {
  // Get all users with optional filtering and pagination
  async getUsers({
    role = null,
    status = null,
    artistType = null,
    search = null,
    page = 1,
    pageSize = 20,
    sortBy = 'created_at',
    sortOrder = 'desc'
  } = {}) {
    try {
      // Use admin client to bypass RLS so all users are visible to admins
      const client = isAdminConfigured() ? getAdminClient() : supabase;

      let query = client?.from('user_profiles')?.select(`
          *,
          user_analytics(
            metric_type,
            metric_value,
            metric_date
          )
        `, { count: 'exact' });

      // Apply filters
      if (role) {
        query = query?.eq('role', role);
      }
      
      if (status) {
        query = query?.eq('status', status);
      }
      
      if (artistType) {
        query = query?.eq('artist_type', artistType);
      }
      
      if (search) {
        query = query?.or(`full_name.ilike.%${search}%,email.ilike.%${search}%,bio.ilike.%${search}%`);
      }

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query?.range(from, to);

      // Apply sorting
      query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error, count } = await query;
      
      if (error) {
        console.error('Database query error:', error);
        throw error;
      }
      
      return {
        users: data || [],
        totalCount: count,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Get user by ID
  async getUserById(userId) {
    try {
      // Use admin client to bypass RLS
      const client = isAdminConfigured() ? getAdminClient() : supabase;

      const { data, error } = await client?.from('user_profiles')?.select(`
          *,
          user_analytics(
            metric_type,
            metric_value,
            metric_date
          ),
          user_activity(
            activity_type,
            description,
            created_at
          )
        `)?.eq('id', userId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  // Enhanced user creation method with better validation and error handling
  async createUser(userData) {
    try {
      const { email, password, full_name, role, artist_type, bio, location, website, status } = userData;
      
      // Input validation
      if (!email || !full_name || !role) {
        throw new Error('Missing required fields: email, full_name, and role are required');
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(email)) {
        throw new Error('Invalid email format. Please provide a valid email address.');
      }

      // Role validation
      const validRoles = ['admin', 'editor', 'artist'];
      if (!validRoles?.includes(role)) {
        throw new Error(`Invalid role. Must be one of: ${validRoles.join(', ')}`);
      }

      // Artist type validation
      if (role === 'artist' && !artist_type) {
        throw new Error('Artist type is required when role is "artist"');
      }

      const validArtistTypes = ['musician', 'visual', 'director', 'actor', 'writer'];
      if (role === 'artist' && !validArtistTypes?.includes(artist_type)) {
        throw new Error(`Invalid artist type. Must be one of: ${validArtistTypes.join(', ')}`);
      }
      
      // Check if admin configuration is available
      if (!isAdminConfigured()) {
        throw new Error(`❌ Admin User Creation Not Configured

**Required Configuration:**
1. Add VITE_SUPABASE_SERVICE_ROLE_KEY to environment variables
2. Enable "User Management" in Supabase Dashboard → Authentication → Settings
3. Configure proper RLS policies for admin user creation
4. Verify project billing status (admin features may require paid plan)

**Current Status:**
❌ VITE_SUPABASE_SERVICE_ROLE_KEY is missing or invalid
❌ Admin operations are not available

**Next Steps:**
1. Contact your system administrator
2. Add the Service Role Key to your project settings
3. Restart the application after configuration

**For System Administrators:**
• Go to Supabase Dashboard → Settings → API
• Copy the "service_role" key (NOT the anon public key)
• Add to environment: VITE_SUPABASE_SERVICE_ROLE_KEY=your-key-here
• Restart the development server`);
      }

      // Generate a secure password if none provided
      const userPassword = password || this.generateSecurePassword();
      
      // Enhanced password validation
      if (password && password?.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      // Verify current user session with retry mechanism
      let currentUser = null;
      let retries = 3;
      
      while (retries > 0 && !currentUser?.data?.user) {
        try {
          currentUser = await supabase?.auth?.getUser();
          if (currentUser?.data?.user) break;
        } catch (authCheckError) {
          console.warn('Auth check attempt failed, retrying...', authCheckError);
        }
        retries--;
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (!currentUser?.data?.user) {
        throw new Error(`⚠️ Authentication Session Required

Your session has expired or is invalid. Please:

1. **Refresh this page** to restore your session
2. **Log out and log back in** as an administrator  
3. **Clear browser cache** and cookies, then retry
4. **Try using an incognito/private window**

If the problem persists, contact your system administrator.`);
      }

      // Use admin client for user creation
      let authData = null;
      let authError = null;
      
      try {
        const adminClient = getAdminClient();
        const result = await adminClient?.auth?.admin?.createUser({
          email: email?.toLowerCase()?.trim(),
          password: userPassword,
          email_confirm: true, // Auto-confirm email for admin-created users
          user_metadata: {
            full_name: full_name?.trim(),
            role,
            artist_type,
            created_by_admin: true,
            created_at: new Date()?.toISOString()
          }
        });
        
        authData = result?.data;
        authError = result?.error;
      } catch (error) {
        authError = error;
        console.error('Admin create user API error:', error);
        handleAdminError(error, 'user creation');
      }

      if (authError) {
        console.error('Auth creation error:', authError);
        
        // Enhanced error handling for specific auth errors
        if (authError?.message?.includes('already exists') || authError?.message?.includes('duplicate') || authError?.message?.includes('already been registered')) {
          throw new Error(`⚠️ Email Already Registered

The email address "${email}" is already associated with an existing account.

**Solutions:**
• Use a different email address for this user
• Check if the user already exists in the user management table
• Contact the existing user to recover their account if needed

**Note:** Each email address can only be used once per platform.`);
        }
        
        handleAdminError(authError, 'user creation');
      }

      if (!authData?.user?.id) {
        throw new Error(`❌ User Creation Failed

No user ID was returned from the authentication service. This may indicate:

**Possible Issues:**
• Service role key permissions are insufficient
• Supabase project configuration error
• Authentication service temporary unavailability

**Solutions:**
1. Verify your Supabase project is active and properly configured
2. Check service role key permissions in Supabase dashboard
3. Try again in a few minutes
4. Contact your system administrator if the problem persists`);
      }

      // Wait for database triggers to complete
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Enhanced profile creation/update logic
      let profileData = null;
      let profileRetries = 5; // Increased retries for profile operations
      
      // Get admin client for profile operations to bypass RLS
      const adminClient = getAdminClient();
      
      while (profileRetries > 0 && !profileData) {
        try {
          // Use admin client to check for existing profile
          const { data: existingProfile, error: profileFetchError } = await adminClient
            ?.from('user_profiles')
            ?.select('*')
            ?.eq('id', authData?.user?.id)
            ?.single();

          if (!profileFetchError && existingProfile) {
            profileData = existingProfile;
            break;
          }
        } catch (error) {
          console.warn(`Profile fetch attempt ${6 - profileRetries} failed, retrying...`, error);
        }
        
        profileRetries--;
        if (profileRetries > 0) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      // Create or update profile with provided data
      const profileUpdateData = {
        full_name: full_name?.trim(),
        email: email?.toLowerCase()?.trim(),
        role,
        bio: bio?.trim() || '',
        location: location?.trim() || '',
        website: website?.trim() || '',
        status: status || 'active',
        updated_at: new Date()?.toISOString()
      };

      // Add artist_type only for artists
      if (role === 'artist' && artist_type) {
        profileUpdateData.artist_type = artist_type;
      }

      if (profileData) {
        // Update existing profile using admin client
        try {
          const { data: updatedProfile, error: profileError } = await adminClient
            ?.from('user_profiles')
            ?.update(profileUpdateData)
            ?.eq('id', authData?.user?.id)
            ?.select()
            ?.single();

          if (!profileError && updatedProfile) {
            profileData = updatedProfile;
          } else {
            console.warn('⚠️ Profile update failed:', profileError);
          }
        } catch (error) {
          console.warn('⚠️ Profile update error:', error);
        }
      } else {
        // Create profile manually using admin client (fallback)
        try {
          const { data: manualProfile, error: manualError } = await adminClient
            ?.from('user_profiles')
            ?.insert({
              id: authData?.user?.id,
              ...profileUpdateData,
              created_at: new Date()?.toISOString()
            })
            ?.select()
            ?.single();

          if (!manualError && manualProfile) {
            profileData = manualProfile;
          } else {
            console.error('❌ Manual profile creation failed:', manualError);
            throw new Error(`Profile Creation Failed: ${manualError?.message || 'Unknown error occurred during profile creation'}`);
          }
        } catch (error) {
          console.error('❌ Manual profile creation error:', error);
          throw new Error(`Profile Creation Error: ${error?.message || 'Failed to create user profile in database'}`);
        }
      }

      // Log successful user creation activity
      try {
        await this.logActivity(
          authData?.user?.id, 
          'user_created', 
          `Admin created user account: ${full_name} (${email}) with role: ${role}${role === 'artist' ? ` - ${artist_type}` : ''}`
        );
      } catch (logError) {
        console.warn('⚠️ Activity logging failed (non-critical):', logError);
      }

      return {
        ...profileData,
        // Add creation metadata for success feedback
        _metadata: {
          created_by: 'admin',
          created_at: new Date()?.toISOString(),
          auth_confirmed: true,
          password_generated: !password
        }
      };

    } catch (error) {
      console.error('❌ Error creating user:', error);
      
      // Enhanced error categorization with user-friendly messages
      if (error?.message?.includes('Missing required fields')) {
        throw new Error('⚠️ Form Validation Error\n\n' + error?.message + '\n\nPlease fill in all required fields and try again.');
      } else if (error?.message?.includes('Invalid email format')) {
        throw new Error('📧 Email Format Error\n\n' + error?.message + '\n\nExample: user@example.com');
      } else if (error?.message?.includes('Invalid role') || error?.message?.includes('Invalid artist type')) {
        throw new Error('🔧 Role Configuration Error\n\n' + error?.message + '\n\nPlease select a valid option from the dropdown.');
      } else if (error?.message?.includes('Password must be')) {
        throw new Error('🔒 Password Security Error\n\n' + error?.message + '\n\nPassword requirements:\n• At least 8 characters long\n• Include uppercase and lowercase letters\n• Include numbers and special characters');
      } else if (error?.message?.includes('duplicate key value') || 
                 error?.message?.includes('already exists') ||
                 error?.message?.includes('already been registered') ||
                 error?.message?.includes('Email Already Registered')) {
        throw new Error(error?.message?.includes('Email Already Registered') 
          ? error?.message 
          : '📧 Email Already Exists\n\nA user with this email already exists. Please use a different email address.');
      } else if (error?.message?.includes('rate limit') || 
                 error?.message?.includes('too many')) {
        throw new Error('⏱️ Rate Limit Exceeded\n\nToo many user creation attempts. Please wait 5 minutes before trying again.\n\nThis helps protect the system from automated attacks.');
      } else if (error?.message?.includes('Admin User Creation Not Configured') ||
                 error?.message?.includes('service role') ||
                 error?.message?.includes('configuration') ||
                 error?.message?.includes('not properly configured')) {
        throw error; // Pass through the already enhanced configuration error
      } else if (error?.message?.includes('not allowed') || 
                 error?.message?.includes('insufficient permissions') ||
                 error?.message?.includes('unauthorized') ||
                 error?.message?.includes('forbidden') ||
                 error?.message?.includes('row-level security policy')) {
        throw new Error(`🔐 Permission Denied

**Access Issue:** Profile creation failed due to insufficient permissions.

**Possible Causes:**
• Service role key is not properly configured
• Row-level security (RLS) policies are blocking the operation
• Admin client is not properly initialized

**Solutions:**
1. Verify VITE_SUPABASE_SERVICE_ROLE_KEY is correctly set in environment variables
2. Restart the application to refresh the admin client configuration
3. Contact your system administrator to verify RLS policies
4. Check that the service role key has proper permissions in Supabase dashboard

**For System Administrators:**
• Verify service role key has full access permissions
• Check RLS policies on user_profiles table
• Ensure admin_full_access_user_profiles policy is active
• Test service role key access in Supabase dashboard`);
      } else if (error?.message?.includes('Authentication Session Required') ||
                 error?.message?.includes('Authentication required') ||
                 error?.message?.includes('session expired')) {
        throw error; // Pass through the already enhanced session error
      } else if (error?.message?.includes('network') ||
                 error?.message?.includes('connection') ||
                 error?.message?.includes('timeout') ||
                 error?.message?.includes('fetch')) {
        throw new Error('🌐 Network Connection Error\n\nCannot connect to the authentication service.\n\n**Troubleshooting:**\n1. Check your internet connection\n2. Verify the Supabase service is available\n3. Try again in a few minutes\n4. Contact technical support if the problem persists');
      } else if (error?.message?.includes('User Creation Failed') ||
                 error?.message?.includes('Profile Creation Failed') ||
                 error?.message?.includes('Profile Creation Error')) {
        throw error; // Pass through the already enhanced creation failure error
      } else {
        // Generic error with helpful context
        throw new Error(`⚠️ User Creation Error

${error?.message || 'An unexpected error occurred while creating the user.'}

**Next Steps:**
1. Try creating the user again
2. If the error persists, note the exact error message
3. Contact your system administrator with the error details
4. Refresh the page if you continue having issues

**Error Context:** User creation process failed during account setup.`);
      }
    }
  }

  // Enhanced password generator with better security
  generateSecurePassword() {
    const length = 14; // Increased length for better security
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*+-=";
    
    let password = "";
    
    // Ensure at least one of each type for security requirements
    password += uppercase?.[Math.floor(Math.random() * uppercase?.length)];
    password += lowercase?.[Math.floor(Math.random() * lowercase?.length)];
    password += numbers?.[Math.floor(Math.random() * numbers?.length)];
    password += symbols?.[Math.floor(Math.random() * symbols?.length)];
    
    // Fill the rest randomly from all character sets
    const allChars = lowercase + uppercase + numbers + symbols;
    for (let i = password?.length; i < length; i++) {
      password += allChars?.[Math.floor(Math.random() * allChars?.length)];
    }
    
    // Shuffle the password to avoid predictable patterns
    return password?.split('')?.sort(() => 0.5 - Math.random())?.join('');
  }

  // Update user profile
  async updateUser(userId, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      
      // Log activity
      await this.logActivity(userId, 'profile_update', 'Profile information updated');
      
      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Update user status (admin only)
  async updateUserStatus(userId, status) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ 
          status,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      
      // Log activity
      await this.logActivity(userId, 'status_change', `Status changed to ${status}`);
      
      return data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Delete user (admin only)
  async deleteUser(userId) {
    try {
      if (!isAdminConfigured()) {
        throw new Error('Admin operations are not configured. Please add VITE_SUPABASE_SERVICE_ROLE_KEY to your environment variables.');
      }

      const adminClient = getAdminClient();
      const { error } = await adminClient?.auth?.admin?.deleteUser(userId);
      
      if (error) {
        handleAdminError(error, 'user deletion');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Bulk update users (admin only)
  async bulkUpdateUsers(userIds, updates) {
    try {
      const promises = userIds?.map(userId => this.updateUser(userId, updates));
      const results = await Promise.allSettled(promises);
      
      const successful = results?.filter(result => result?.status === 'fulfilled')?.length;
      const failed = results?.filter(result => result?.status === 'rejected')?.length;
      
      return {
        successful,
        failed,
        total: userIds?.length
      };
    } catch (error) {
      console.error('Error bulk updating users:', error);
      throw error;
    }
  }

  // Get user metrics/analytics
  async getUserMetrics(userId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('user_analytics')?.select('*')?.eq('user_id', userId)?.order('metric_date', { ascending: false });

      if (startDate) {
        query = query?.gte('metric_date', startDate);
      }
      
      if (endDate) {
        query = query?.lte('metric_date', endDate);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user metrics:', error);
      throw error;
    }
  }

  // Get platform-wide analytics (admin only)
  async getPlatformAnalytics() {
    try {
      // Get user counts by role
      const { data: roleCounts, error: roleError } = await supabase?.from('user_profiles')?.select('role')?.then(({ data, error }) => {
          if (error) throw error;
          return {
            data: data?.reduce((acc, user) => {
              acc[user?.role] = (acc?.[user?.role] || 0) + 1;
              return acc;
            }, {}),
            error: null
          };
        });

      if (roleError) throw roleError;

      // Get user counts by status
      const { data: statusCounts, error: statusError } = await supabase?.from('user_profiles')?.select('status')?.then(({ data, error }) => {
          if (error) throw error;
          return {
            data: data?.reduce((acc, user) => {
              acc[user?.status] = (acc?.[user?.status] || 0) + 1;
              return acc;
            }, {}),
            error: null
          };
        });

      if (statusError) throw statusError;

      // Get total user count
      const { count: totalUsers, error: countError } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      // Get recent registrations (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      
      const { count: recentRegistrations, error: recentError } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.gte('created_at', thirtyDaysAgo?.toISOString());

      if (recentError) throw recentError;

      return {
        totalUsers,
        roleCounts,
        statusCounts,
        recentRegistrations,
        growthRate: totalUsers > 0 ? ((recentRegistrations / totalUsers) * 100)?.toFixed(1) : 0
      };
    } catch (error) {
      console.error('Error fetching platform analytics:', error);
      throw error;
    }
  }

  // Log user activity
  async logActivity(userId, activityType, description, metadata = {}) {
    try {
      // Use admin client to bypass RLS when logging activity for other users
      // This is necessary when admins create users or perform actions on behalf of others
      const adminClient = getAdminClient();
      
      const { data, error } = await adminClient
        ?.from('user_activity')
        ?.insert({
          user_id: userId,
          activity_type: activityType,
          description,
          metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.warn('⚠️ Activity logging failed (non-critical):', error);
        // Don't throw - activity logging shouldn't break main operations
        return null;
      }
      
      return data;
    } catch (error) {
      console.warn('⚠️ Activity logging error (non-critical):', error);
      // Don't throw - activity logging shouldn't break main operations
      return null;
    }
  }

  // Get user activity log
  async getUserActivity(userId, limit = 50) {
    try {
      const { data, error } = await supabase?.from('user_activity')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user activity:', error);
      throw error;
    }
  }

  // Search users
  async searchUsers(searchTerm, filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,bio.ilike.%${searchTerm}%`)?.order('created_at', { ascending: false });

      // Apply additional filters
      Object.entries(filters)?.forEach(([key, value]) => {
        if (value) {
          query = query?.eq(key, value);
        }
      });

      const { data, error } = await query?.limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  // Resend invitation email (admin only)
  async resendInvitation(userId, userEmail) {
    try {
      if (!isAdminConfigured()) {
        throw new Error('Admin operations are not configured. Please add VITE_SUPABASE_SERVICE_ROLE_KEY to your environment variables.');
      }

      const adminClient = getAdminClient();
      const { data, error } = await adminClient?.auth?.admin?.generateLink({
        type: 'signup',
        email: userEmail,
        options: {
          redirectTo: `${window.location?.origin}/login`
        }
      });

      if (error) {
        handleAdminError(error, 'invitation resend');
      }

      // Log activity
      await this.logActivity(userId, 'invitation_resent', 'Invitation email was resent');

      return data;
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error;
    }
  }

  // Update user password (admin only)
  async updateUserPassword(userEmail, newPassword) {
    try {
      if (!userEmail || !newPassword) {
        throw new Error('Email and new password are required');
      }

      // Password validation
      if (newPassword?.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const { data, error } = await supabase?.rpc('admin_update_user_password', {
        target_user_email: userEmail?.toLowerCase()?.trim(),
        new_password: newPassword
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to update password');
      }

      return {
        success: true,
        message: data?.message,
        user_id: data?.user_id,
        email: data?.email
      };
    } catch (error) {
      console.error('Error updating user password:', error);
      
      // Enhanced error handling
      if (error?.message?.includes('Unauthorized')) {
        throw new Error('🔐 Access Denied\n\nYou do not have permission to change user passwords. Only administrators can perform this action.');
      } else if (error?.message?.includes('User not found')) {
        throw new Error(`📧 User Not Found\n\nNo user found with email address: ${userEmail}\n\nPlease verify the email address is correct.`);
      } else if (error?.message?.includes('Password must be')) {
        throw new Error('🔒 Password Requirements\n\n' + error?.message + '\n\nPassword must be at least 6 characters long and contain a mix of letters, numbers, and symbols.');
      } else if (error?.message?.includes('Database error')) {
        throw new Error('💾 Database Error\n\nThere was a problem accessing the database. Please try again later or contact technical support.');
      } else {
        throw new Error(`⚠️ Password Update Failed\n\n${error?.message || 'An unexpected error occurred while updating the password.'}\n\nPlease try again or contact your system administrator.`);
      }
    }
  }

  // Reset user password with optional generated password (admin only)
  async resetUserPassword(userEmail, generatePassword = true) {
    try {
      if (!userEmail) {
        throw new Error('Email address is required');
      }

      const { data, error } = await supabase?.rpc('admin_reset_user_password', {
        target_user_email: userEmail?.toLowerCase()?.trim(),
        generate_new_password: generatePassword
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to reset password');
      }

      return {
        success: true,
        message: data?.message,
        user_id: data?.user_id,
        email: data?.email,
        generated_password: data?.generated_password,
        password_generated: data?.password_generated
      };
    } catch (error) {
      console.error('Error resetting user password:', error);
      
      // Enhanced error handling
      if (error?.message?.includes('Unauthorized')) {
        throw new Error('🔐 Access Denied\n\nYou do not have permission to reset user passwords. Only administrators can perform this action.');
      } else if (error?.message?.includes('User not found')) {
        throw new Error(`📧 User Not Found\n\nNo user found with email address: ${userEmail}\n\nPlease verify the email address is correct.`);
      } else {
        throw new Error(`⚠️ Password Reset Failed\n\n${error?.message || 'An unexpected error occurred while resetting the password.'}\n\nPlease try again or contact your system administrator.`);
      }
    }
  }

  // Get user information by email (admin only)
  async getUserByEmail(userEmail) {
    try {
      if (!userEmail) {
        throw new Error('Email address is required');
      }

      const { data, error } = await supabase?.rpc('admin_get_user_by_email', {
        target_user_email: userEmail?.toLowerCase()?.trim()
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to get user information');
      }

      return {
        success: true,
        user: data?.user
      };
    } catch (error) {
      console.error('Error getting user by email:', error);
      
      // Enhanced error handling
      if (error?.message?.includes('Unauthorized')) {
        throw new Error('🔐 Access Denied\n\nYou do not have permission to access user information. Only administrators can perform this action.');
      } else if (error?.message?.includes('User not found')) {
        throw new Error(`📧 User Not Found\n\nNo user found with email address: ${userEmail}\n\nPlease verify the email address is correct.`);
      } else {
        throw new Error(`⚠️ User Lookup Failed\n\n${error?.message || 'An unexpected error occurred while looking up the user.'}\n\nPlease try again or contact your system administrator.`);
      }
    }
  }

  // Bulk password reset for multiple users (admin only)
  async bulkResetPasswords(userEmails, generatePasswords = true) {
    try {
      if (!userEmails || userEmails?.length === 0) {
        throw new Error('At least one email address is required');
      }

      const results = [];
      const errors = [];

      for (const email of userEmails) {
        try {
          const result = await this.resetUserPassword(email, generatePasswords);
          results?.push({
            email,
            success: true,
            ...result
          });
        } catch (error) {
          errors?.push({
            email,
            success: false,
            error: error?.message
          });
        }
      }

      return {
        total: userEmails?.length,
        successful: results?.length,
        failed: errors?.length,
        results,
        errors
      };
    } catch (error) {
      console.error('Error in bulk password reset:', error);
      throw error;
    }
  }

  // Change password for current authenticated user
  async changeOwnPassword(currentPassword, newPassword) {
    try {
      if (!currentPassword || !newPassword) {
        throw new Error('Current password and new password are required');
      }

      // Password validation
      if (newPassword?.length < 8) {
        throw new Error('New password must be at least 8 characters long');
      }

      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;

      return {
        success: true,
        message: 'Password updated successfully',
        user: data?.user
      };
    } catch (error) {
      console.error('Error changing own password:', error);
      
      // Enhanced error handling
      if (error?.message?.includes('New password should be different')) {
        throw new Error('🔒 Password Requirement\n\nYour new password must be different from your current password.');
      } else if (error?.message?.includes('Password should be at least')) {
        throw new Error('🔒 Password Requirements\n\nPassword must be at least 8 characters long and contain a mix of uppercase, lowercase, numbers, and special characters.');
      } else if (error?.message?.includes('Invalid login credentials')) {
        throw new Error('🔐 Authentication Error\n\nYour current password is incorrect. Please verify and try again.');
      } else {
        throw new Error(`⚠️ Password Change Failed\n\n${error?.message || 'An unexpected error occurred while changing your password.'}\n\nPlease try again or contact support.`);
      }
    }
  }

  // Add specific method for updating artist type (admin only)
  async updateArtistType(userId, artistType) {
    try {
      if (!artistType) {
        throw new Error('Artist type is required');
      }

      const validArtistTypes = ['musician', 'visual', 'director', 'actor', 'writer'];
      if (!validArtistTypes?.includes(artistType)) {
        throw new Error(`Invalid artist type. Must be one of: ${validArtistTypes.join(', ')}`);
      }

      const { data, error } = await supabase?.from('user_profiles')?.update({
          artist_type: artistType,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      
      // Log activity
      await this.logActivity(userId, 'artist_type_change', `Artist type changed to ${artistType}`);
      
      return data;
    } catch (error) {
      console.error('Error updating artist type:', error);
      
      // Enhanced error handling
      if (error?.message?.includes('Invalid artist type')) {
        throw new Error(`🎨 Invalid Artist Type\n\n${error?.message}\n\nValid options: Musician, Visual, Director, Actor, Writer`);
      } else if (error?.message?.includes('User not found')) {
        throw new Error('👤 User Not Found\n\nThe specified user could not be found. They may have been deleted or the ID is incorrect.');
      } else if (error?.message?.includes('permission') || error?.message?.includes('unauthorized')) {
        throw new Error('🔐 Access Denied\n\nYou do not have permission to change artist types. Only administrators can perform this action.');
      } else {
        throw new Error(`⚠️ Artist Type Update Failed\n\n${error?.message || 'An unexpected error occurred while updating the artist type.'}\n\nPlease try again or contact your system administrator.`);
      }
    }
  }

  // Get all user activity logs with advanced filtering (admin only)
  async getAllUserActivity({
    userId = null,
    role = null,
    activityType = null,
    search = null,
    startDate = null,
    endDate = null,
    page = 1,
    pageSize = 50,
    sortBy = 'created_at',
    sortOrder = 'desc'
  } = {}) {
    try {
      // Build query with user profile join for comprehensive data
      let query = supabase
        ?.from('user_activity')
        ?.select(`
          *,
          user_profiles (
            id,
            full_name,
            email,
            role,
            status
          )
        `, { count: 'exact' });

      // Apply user filter
      if (userId) {
        query = query?.eq('user_id', userId);
      }

      // Apply role filter (via user_profiles join)
      if (role) {
        query = query?.eq('user_profiles.role', role);
      }

      // Apply activity type filter
      if (activityType) {
        query = query?.eq('activity_type', activityType);
      }

      // Apply date range filters
      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      // Apply search filter across multiple fields
      if (search) {
        query = query?.or(
          `description.ilike.%${search}%,activity_type.ilike.%${search}%,user_profiles.full_name.ilike.%${search}%,user_profiles.email.ilike.%${search}%`
        );
      }

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query?.range(from, to);

      // Apply sorting
      query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching user activity:', error);
        throw error;
      }

      return {
        activities: data || [],
        totalCount: count,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    } catch (error) {
      console.error('Error fetching all user activity:', error);
      throw error;
    }
  }

  // Get distinct activity types for filter dropdown
  async getActivityTypes() {
    try {
      const { data, error } = await supabase
        ?.from('user_activity')
        ?.select('activity_type')
        ?.order('activity_type');

      if (error) throw error;

      // Get unique activity types
      const uniqueTypes = [...new Set(data?.map(item => item?.activity_type))];
      return uniqueTypes?.filter(type => type); // Remove null/undefined
    } catch (error) {
      console.error('Error fetching activity types:', error);
      throw error;
    }
  }

  // Export activity logs to CSV
  async exportActivityLogs(filters = {}) {
    try {
      // Fetch all matching activities without pagination
      const { activities } = await this.getAllUserActivity({
        ...filters,
        pageSize: 10000 // Large number to get all records
      });

      // Convert to CSV format
      const csvHeaders = [
        'Timestamp',
        'User Name',
        'Email',
        'Role',
        'Action Type',
        'Description',
        'IP Address',
        'User Agent'
      ];

      const csvRows = activities?.map(activity => [
        new Date(activity?.created_at)?.toLocaleString(),
        activity?.user_profiles?.full_name || 'N/A',
        activity?.user_profiles?.email || 'N/A',
        activity?.user_profiles?.role || 'N/A',
        activity?.activity_type || 'N/A',
        activity?.description || 'N/A',
        activity?.ip_address || 'N/A',
        activity?.user_agent || 'N/A'
      ]);

      // Create CSV content
      const csvContent = [
        csvHeaders?.join(','),
        ...csvRows?.map(row => 
          row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(',')
        )
      ]?.join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link?.setAttribute('href', url);
      link?.setAttribute('download', `user_activity_logs_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);

      return { success: true, count: activities?.length };
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
export default userService;